// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.TinyMCE.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Config from 'config';
import tinymce from 'tinymce/tinymce';
import 'tinymce/themes/modern/theme';
import TinyMCEConfig from './Izenda.TinyMCEConfig';
import uiConstants from 'logic/data/Izenda.UIConstants';
import { FORMAT_CONFIG } from 'logic/data/Izenda.ReportPartFormConstants';
import constants from 'logic/data/Izenda.UIConstants';
import TinyMCEConstants from 'logic/data/Izenda.TinyMCEConstants';
import localizer from 'share/localization/Izenda.Localizer';
import { clone, isNil, isEqual, isEmpty, assignIn, forEach, findKey, chain, includes, head } from 'lodash/fp';

const existingIndex = 0;
const secondIndex = 1;
const HANDLER_NAMES = TinyMCEConfig.EVENTS.map(
  event => 'on' + head(event).toUpperCase() + event.substring(secondIndex)
);

class TinyMCE extends Component {
  static propTypes = {
    config: PropTypes.object,
    content: PropTypes.string,
    id: PropTypes.string
  };

  static defaultProps = {
    config: {},
    content: ''
  };

  constructor(props: any) {
    super(props);
    const { id, value } = props;

    this.state = {
      id,
      value
    };

    this.tinymce = tinymce;
    this.id = this.id || id;
  }

  componentDidMount() {
    const config = clone(this.props.config);
    this.initTinyMCE(config);
  }

  componentWillReceiveProps(nextProps: any) {
    if (!isEqual(this.props.config, nextProps.config)) {
      this.initTinyMCE(clone(nextProps.config), nextProps.content);
    }
    if (!isEqual(this.props.id, nextProps.id)) {
      this.id = nextProps.id;
    }
    if (!isEqual(this.props.content, nextProps.content)) {
      this.tinymce.get(this.id).setContent(nextProps.content);
    }

    this.toggleShowInvisibleChars(nextProps);
  }

  toggleShowInvisibleChars = (props: any) => {
    // Toggle support show invisible character
    if (props.visualChars !== this.props.visualChars) {
      this.tinymce.activeEditor.execCommand('mceVisualChars', true, null, { skip_focus: true });
    }
  };

  shouldComponentUpdate(nextProps: any) {
    return !isEqual(this.props.content, nextProps.content) || !isEqual(this.props.config, nextProps.config);
  }

  componentWillUnmount() {
    this.removeTinyMCE();
  }

  initTinyMCE = (config: any, content: string) => {
    if (this.isInit) {
      this.removeTinyMCE();
    }

    const context = this;

    this.node.style.hidden = 'hidden';
    const setupCallback = config.setup;
    const hasSetupCallback = typeof setupCallback === 'function';

    config.pagebreak_separator = TinyMCEConstants.PAGE_BREAK_HTML;
    config.selector = `#${this.id}`;
    config = assignIn(TinyMCEConfig.DEFAULT_CONFIG, config);

    if (!isEmpty(Config.RootPath)) {
      this.tinymce.baseURL = Config.RootPath;
    }

    if (!isEmpty(Config.CssFile)) {
      config.content_css = `${Config.RootPath}/${Config.CssFile}`;
    }

    config.setup = (editor: any) => {
      TinyMCEConfig.EVENTS.forEach((event: any, index: number) => {
        const handler = context.props[HANDLER_NAMES[index]];
        if (typeof handler !== 'function') return;

        editor.on(event, (e: any) => {
          handler(e, editor);
        });
      });

      editor.on('NodeChange', (e: any) => {
        context.handleNodeChange(e, editor);
      });

      editor.on('init', () => {
        TinyMCEConfig.FORMAT_CHANGES.forEach((name: string) => {
          editor.formatter.formatChanged(name, (state: any, data: any) => {
            context.props.onFormatChange && context.props.onFormatChange(data.format, state);
          });
        });

        if (!isNil(content)) {
          editor.setContent(content);
        }

        if (context.props.visualChars) {
          context.tinymce.activeEditor.execCommand('mceVisualChars', true, null, { skip_focus: true });
        }
      });

      editor.addMenuItem('spell_checker', {
        text: localizer.getText('TINY_MCE_SPELL_CHECKER'),
        onclick: context.handleSpellCheck,
        onPostRender: context.handleDisabledState
      });

      editor.addMenuItem('label', {
        text:  'Label Field',
        onclick: context.props.labelHandler,
      });

      editor.addMenuItem('metric', {
        text: 'Metric Field',
        onclick: context.props.metricHandler,
      });

      editor.on('contextmenu', () => {
        context.handleContextMenu();
      });

      // Fix issues on Firefox
      editor.on('BeforeSetContent', (e: any) => {
        e.content = e.content.replace('<br _moz_editor_bogus_node="TRUE" />', '');
      });

      if (hasSetupCallback) {
        setupCallback(editor);
      }
    };

    this.tinymce.init(config);
    this.node.style.hidden = '';
    this.isInit = true;
  };

  handleContextMenu = () => {
    if (isNil(this.spellCheckItem)) {
      return;
    }
    this.checkContentSelected();
  };

  handleDisabledState = (item: any) => {
    this.spellCheckItem = item.control;
    this.checkContentSelected();
  };

  checkContentSelected = () => {
    const selectedContent = this.tinymce.get(this.id).selection.getContent();
    if (!selectedContent || $izenda(`<div>${selectedContent}</div>`).find('field').length) {
      this.spellCheckItem.disabled(true);
    } else {
      this.spellCheckItem.disabled(false);
    }
  };

  handleSpellCheck = () => {
    if (this.props.onSpellCheck) {
      const selection = this.tinymce.get(this.id).selection;
      const selectionContent = selection.getContent();
      const selectedRange = selection.getRng();

      this.props.onSpellCheck(selectionContent, selectedRange.startOffset, selectedRange.endOffset);
    }
  };

  removeElement = (element: any) => {
    this.tinymce.get(this.id).dom.remove(element);
  };

  handleNodeChange = (e: any) => {
    const props = this.props;
    const fontSettings = {};

    const size = this.getFormatValue(e, FORMAT_CONFIG.FONT_SIZE, 'fontsize');
    const fontname = this.getFormatValue(e, FORMAT_CONFIG.FONT_NAME, 'fontname');

    fontSettings.fontSize = size ? size.replace(constants.defaultUnitType, '') : constants.defaultFontSize;
    fontSettings.fontFamily = fontname
      ? findKey(val => val === fontname, FORMAT_CONFIG.FONT_NAME)
      : constants.defaultFontFamily;

    fontSettings.textColor = this.getCurrentColor('forecolor') || uiConstants.textColor;
    fontSettings.backgroundColor = this.getCurrentColor('hilitecolor') || uiConstants.backgroundColor;
    fontSettings.bullet = this.getSelectionList(e, 'ul');
    fontSettings.numbered = this.getSelectionList(e, 'ol');

    if (props.onFormatChange) {
      for (var key in fontSettings) {
        if (fontSettings.hasOwnProperty(key)) {
          props.onFormatChange(key, fontSettings[key]);
        }
      }
    }
  };

  getSelectionList = (e: any, type: any) => {
    const editor = this.tinymce.get(this.id);
    const parents = editor.dom.getParent(editor.selection.getNode(), type);
    return editor.dom.getStyle(parents, 'listStyleType') || '';
  };

  getCurrentColor = (format: string) => {
    let color = '';
    const editor = tinymce.get(this.id);

    editor.dom.getParents(editor.selection.getStart(), (elm: any) => {
      const value = elm.style && elm.style[format == 'forecolor' ? 'color' : 'background-color'];
      if (value) {
        color = String.rgbToHex(value);
      }
    });

    return color;
  };

  getFormatValue = (e: any, items: Array<any>, formatName: any) => {
    const formatter = this.tinymce.get(this.id).formatter;
    let value = null;

    forEach((node: any) => {
      forEach((item: any) => {
        if (formatter.matchNode(node, formatName, { value: item })) {
          value = item;
        }
      }, items);
    }, e.parents);

    return value;
  };

  removeTinyMCE = () => {
    if (this.tinymce.get(this.id).getBody()) {
      this.tinymce.EditorManager.execCommand('mceRemoveEditor', true, this.id);
    }
    this.isInit = false;
  };

  insertContent = (content: string) => {
    this.tinymce.get(this.id).execCommand('mceInsertContent', false, content);
  };

  replaceContent(node: any, content: string) {
    node.execCommand('mceReplaceContent', false, content);
  }

  toggleFormat = (fmt: any) => {
    this.tinymce.get(this.id).execCommand('mceToggleFormat', false, fmt);
  };

  applyFormat = (format: any, value: any) => {
    const editor = this.tinymce.get(this.id);
    editor.undoManager.transact(function() {
      editor.focus();
      editor.formatter.apply(format, { value: value });
      editor.nodeChanged();
    });
  };

  applyListFormat = (listName: string, styleValue: string) => {
    const editor = this.tinymce.get(this.id);

    editor.undoManager.transact(function() {
      let list,
        dom = editor.dom,
        sel = editor.selection;
      list = dom.getParent(sel.getNode(), 'ol,ul');

      if (!list || list.nodeName !== listName || styleValue === '') {
        var detail = { 'list-style-type': styleValue };
        editor.execCommand(listName === 'UL' ? 'InsertUnorderedList' : 'InsertOrderedList', false, detail);
      }

      list = dom.getParent(sel.getNode(), 'ol,ul');
      if (list) {
        dom.setStyle(list, 'listStyleType', styleValue ? styleValue : null);
        list.removeAttribute('data-mce-style');
      }

      editor.focus();
    });
  };

  insertContentDnD = (content: string) => {
    const editor = this.tinymce.get(this.id);
    const body = editor.getBody();
    const { dom, selection } = editor;
    let range = selection.getRng();

    // user don't set selection, move selection to inside body lastChild
    if (
      (range.startContainer.nodeName === 'BODY' && body.firstChild.firstChild.nodeName === 'STYLE') ||
      range.startContainer.nodeName === 'STYLE'
    ) {
      range = dom.createRng();
      range.setStartAfter(body.lastChild);
      range.setEndAfter(body.lastChild);
      selection.setRng(range);
    }

    editor.execCommand('mceInsertContent', false, content);
  };

  /**
   * Update repeater.
   * @param {string} repeater type
   */
  updateRepeater = (repeaterType: string) => {
    const editor = this.tinymce.get(this.id);
    switch (repeaterType) {
      case 'Add': {
        const selectionContent = editor.selection.getContent();
        if (selectionContent.indexOf('<field') >= existingIndex) {
          const repeaterContent = TinyMCEConstants.REPEATER_START + selectionContent + TinyMCEConstants.REPEATER_END;
          let content = this.getContent();
          if (content.indexOf(selectionContent) < existingIndex) {
            editor.insertContent(repeaterContent);
          } else {
            content = content.replace(selectionContent, repeaterContent);
            editor.setContent(content);
          }
        }
        editor.focus();
        break;
      }
      case 'Remove':
        this.removeRepeater(editor);
        break;
    }
  };

  getContent = () => this.tinymce.get(this.id).getContent();

  /**
   * Remove repeater.
   */
  removeRepeater = (editor: any) => {
    let selectionContent = editor.selection.getContent();

    if (selectionContent.indexOf(TinyMCEConstants.REPEATER_START) >= existingIndex) {
      editor.focus();
      $izenda('repeater', editor.selection.getNode()).unwrap();
    } else {
      const node = this.tinymce.get(this.id).selection.getNode();
      if (node.nodeName === 'REPEATER') {
        selectionContent = $izenda(this.tinymce.get(this.id).selection.getNode().parentNode).html();
        selectionContent = chain(selectionContent)
          .split(TinyMCEConstants.REPEATER_START)
          .join('')
          .split(TinyMCEConstants.REPEATER_END)
          .join('')
          .value();

        editor.focus();
        this.tinymce.get(this.id).selection.getNode().parentNode.innerHTML = selectionContent;
      }
    }
  };

  /**
   * Insert smart tag.
   * @param {string} smart tag type
   * @param {string} value
   */
  insertSmartTag = (smartTagType: string, value: string) => {
    const editor = this.tinymce.get(this.id);
    editor.focus();

    switch (smartTagType) {
      case 'DateTime':
        this.insertContent(
          '<datetime class="field-wrapper"><span class="icon-attached"></span>' + value + '</datetime>'
        );
        break;
      case 'Subtotal':
        this.insertContent(
          '<subtotal class="field-wrapper"><span class="icon-subtotal"></span>Click here to select field</subtotal>'
        );
        break;
      case 'GrandTotal':
        this.insertContent(
          '<grandtotal class="field-wrapper">' +
            '<span class="icon-grandtotal"></span>Click here to select field</grandtotal>'
        );
        break;
    }
  };

  execCommand = (cmd: any, value: any) => {
    this.tinymce.get(this.id).execCommand(cmd, false, value);
    const isNeedUpdateContent = includes(cmd, ['Undo', 'Redo']);
    if (isNeedUpdateContent) {
      this.props.onChange && this.props.onChange({ target: this.tinymce.get(this.id) });
    }
  };

  isSelectedCell = () => {
    const editor = this.tinymce.get(this.id);
    if (!editor.dom) {
      return false;
    }
    const selectedCells = editor.dom.select('td[data-mce-selected],th[data-mce-selected]');
    let selectedElm = head(selectedCells);
    if (!selectedElm) {
      selectedElm = editor.selection.getStart();
    }

    return !!editor.dom.getParent(selectedElm, 'td,th');
  };

  insertTable = (cols: number, rows: number) => {
    const editor = this.tinymce.get(this.id);
    const baseY = 0;
    const baseX = 0;
    let y, x, html, tableElm;
    html = '<table id="__mce"><tbody>';
    for (y = baseY; y < rows; y++) {
      html += '<tr>';
      for (x = baseX; x < cols; x++) {
        html += '<td><br></td>';
      }
      html += '</tr>';
    }
    html += '</tbody></table>';

    editor.undoManager.transact(function() {
      editor.insertContent(html);
      tableElm = editor.dom.get('__mce');
      editor.dom.setAttrib(tableElm, 'id', null);
      editor.dom.setAttribs(tableElm, editor.settings.table_default_attributes || {});
      editor.dom.setStyles(tableElm, editor.settings.table_default_styles || {});
    });

    return tableElm;
  };

  updateNodeRef = (node: any) => {
    this.node = node;
  };

  render() {
    const { className, content } = this.props;
    return <textarea id={this.id} className={className} defaultValue={content} ref={this.updateNodeRef} />;
  }
}

export default TinyMCE;



// WEBPACK FOOTER //
// ./ui/share/components/controls/TinyMCE/Izenda.TinyMCE.js